import React, { useRef, useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { AUTOMOBILE_LOGO_BOX_CLASS } from '../../../types/automobile';
import {
  DEFAULT_MODEL_IMAGE_FRAME,
  MODEL_IMAGE_ZOOM_MAX,
  MODEL_IMAGE_ZOOM_MIN,
  normalizeModelImageFrame,
  panModelImageFrame,
  type AutomobileModelImageFrame,
} from '../../../utils/automobileModelImageFrame';
import AutomobileBrandLogoImage from '../AutomobileBrandLogoImage';

type Props = {
  src: string | null;
  alt: string;
  frame: AutomobileModelImageFrame;
  onChange: (frame: AutomobileModelImageFrame) => void;
  previewFailed: boolean;
  onPreviewError: () => void;
  mutedClass: string;
};

const TRANSPARENCY_GRID =
  'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%) 50% / 16px 16px';

const AutomobileLogoFrameEditor: React.FC<Props> = ({
  src,
  alt,
  frame,
  onChange,
  previewFailed,
  onPreviewError,
  mutedClass,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const setFrame = (patch: Partial<AutomobileModelImageFrame>) => {
    onChange(normalizeModelImageFrame({ ...frame, ...patch }));
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!src) return;
    dragRef.current = { x: event.clientX, y: event.clientY };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || !boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    dragRef.current = { x: event.clientX, y: event.clientY };
    onChange(panModelImageFrame(frame, dx, dy, rect.width, rect.height));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div className="space-y-3">
      <div
        ref={boxRef}
        className={`relative max-w-full rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${AUTOMOBILE_LOGO_BOX_CLASS} ${
          frame.zoom < 100 ? '' : 'bg-transparent'
        } ${src ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : 'bg-gray-50 dark:bg-gray-900'}`}
        style={
          frame.zoom < 100
            ? {
                backgroundImage: TRANSPARENCY_GRID,
              }
            : undefined
        }
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {src && !previewFailed ? (
          <AutomobileBrandLogoImage
            src={src}
            alt={alt}
            frame={frame}
            onError={onPreviewError}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400 px-2">
            <ImageIcon size={24} />
            <span className="text-xs text-center">
              {src && previewFailed ? 'Logo yüklenemedi' : 'URL veya dosya seçin'}
            </span>
          </div>
        )}
        {src && !previewFailed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/45 to-transparent px-2 py-1">
            <p className="text-[10px] text-white/90">Sürükleyerek konumlandırın</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
        <label className={`text-xs ${mutedClass} flex flex-col gap-1`}>
          Ölçek (yakınlaştır / uzaklaştır)
          <input
            type="range"
            min={MODEL_IMAGE_ZOOM_MIN}
            max={MODEL_IMAGE_ZOOM_MAX}
            step={5}
            value={frame.zoom}
            onChange={(e) => setFrame({ zoom: Number(e.target.value) })}
            className="w-full"
            disabled={!src}
          />
          <span className="tabular-nums">
            {Math.round(frame.zoom)}%
            {frame.zoom < 100 ? ' · uzaklaştırıldı' : frame.zoom > 100 ? ' · yakınlaştırıldı' : ' · normal'}
          </span>
        </label>
        <label className={`text-xs ${mutedClass} flex flex-col gap-1`}>
          Yatay konum
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={frame.focalX}
            onChange={(e) => setFrame({ focalX: Number(e.target.value) })}
            className="w-full"
            disabled={!src}
          />
          <span className="tabular-nums">{Math.round(frame.focalX)}%</span>
        </label>
        <label className={`text-xs ${mutedClass} flex flex-col gap-1`}>
          Dikey konum
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={frame.focalY}
            onChange={(e) => setFrame({ focalY: Number(e.target.value) })}
            className="w-full"
            disabled={!src}
          />
          <span className="tabular-nums">{Math.round(frame.focalY)}%</span>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 max-w-xl">
        <button
          type="button"
          className="text-xs px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-40"
          disabled={!src}
          onClick={() => onChange({ ...DEFAULT_MODEL_IMAGE_FRAME })}
        >
          Kadrajı sıfırla
        </button>
        <p className={`text-xs ${mutedClass}`}>
          Önizleme sitedeki marka listesi logosuyla aynı boyuttur. %100 altında PNG şeffaf kalır.
        </p>
      </div>
    </div>
  );
};

export default AutomobileLogoFrameEditor;
